import { useBoardCamera } from '../components/useBoardCamera.js';
import { BackgammonBoard } from "@chessmate/board-3d";
import { pipCount } from "@chessmate/game-engine";
import type { Turn } from "@chessmate/protocol";
import { Icon } from "@chessmate/ui";
import { Canvas } from "@react-three/fiber";
import { useState } from "react";
import { BackgammonTapLayer } from "../components/BackgammonTapLayer.js";
import { PlayerBar, useDuelIdentity } from "../components/table/PlayerBar.js";
import {
  RoundSummaryPanel,
  type RoundSummary,
} from "../components/table/RoundSummaryPanel.js";
import { Sidebar } from "../components/Sidebar.js";
import { useBackgammonBubble } from "../components/useBackgammonBubble.js";
import { useBackgammonHints } from "../components/useBackgammonHints.js";
import { useBoardReady } from "../components/useBoardReady.js";
import { useT } from "../i18n/useT.js";
import {
  backgammonStake,
  backgammonWinKey,
  canDouble,
  pendingOfferAgainstMe,
  useBackgammonStore,
} from "../state/backgammon-store.js";

/**
 * The backgammon screen. Dedicated, per the wiring proposal: it does not go
 * through `GameScreen`/`game-store` because those are chess-shaped end to end
 * (FEN snapshots, `tap(square)`, `MoveEngine`). The store is
 * `backgammon-store.ts`; this screen is its only consumer.
 *
 * The player is always white — same default as chess/checkers, and the board's
 * orientation (viewer at white's seat) is pinned to it.
 */
export function BackgammonScreen({ onExit }: { onExit: () => void }) {
  const boardCam = useBoardCamera("backgammon");
  const { t, i18n } = useT("games");
  const game = useBackgammonStore((s) => s.game);
  const selected = useBackgammonStore((s) => s.selected);
  const targets = useBackgammonStore((s) => s.targets);
  const phase = useBackgammonStore((s) => s.phase);
  const robotThinking = useBackgammonStore((s) => s.robotThinking);
  const result = useBackgammonStore((s) => s.result);
  const roll = useBackgammonStore((s) => s.roll);
  const offerDouble = useBackgammonStore((s) => s.offerDouble);
  const takeCube = useBackgammonStore((s) => s.takeCube);
  const passCube = useBackgammonStore((s) => s.passCube);
  const tap = useBackgammonStore((s) => s.tap);
  const undo = useBackgammonStore((s) => s.undo);
  const restart = useBackgammonStore((s) => s.restart);
  const online = useBackgammonStore((s) => s.online);
  const submitting = useBackgammonStore((s) => s.submitting);
  const waiting = useBackgammonStore((s) => s.waiting);
  const robotDepth = useBackgammonStore((s) => s.robotDepth);
  const setRobotDepth = useBackgammonStore((s) => s.setRobotDepth);
  const clock = useBackgammonStore((s) => s.clock);
  const candidates = useBackgammonStore((s) => s.candidates);
  const [overlayTransform, setOverlayTransform] = useState<string>();
  const played = useBackgammonStore((s) => s.played);
  const [boardReady, markBoardReady] = useBoardReady();
  // Cube gates (online only; offline never doubles — no robot cube policy).
  const doubles = useBackgammonStore((s) => canDouble(s));
  const offerAgainstMe = useBackgammonStore((s) => pendingOfferAgainstMe(s));

  const who = useDuelIdentity(t("table-you"), t("table-opponent"));

  // Assist hints (hooks must run before the early return below). Same gate as
  // the roll/tap guards: my seat, dice rolled, nothing pending, not spectating.
  const hintSeat: Turn = online?.side ?? "w";
  const canPlayDice =
    game != null &&
    phase === "playing" &&
    game.position.turn === hintSeat &&
    game.dice !== null &&
    !game.cubeOffer &&
    !robotThinking &&
    !submitting &&
    !waiting &&
    !online?.spectating;
  const { movable, risks } = useBackgammonHints(
    game?.position ?? null,
    candidates,
    played.length,
    selected,
    canPlayDice,
  );

  const locale = ((i18n.language || "en").slice(0, 2) === "fr" ? "fr" : (i18n.language || "en").slice(0, 2) === "es" ? "es" : "en") as "en" | "fr" | "es";
  const botBubble = useBackgammonBubble({
    enabled: game != null && !online,
    phase,
    barMine: game?.position.bar.w ?? 0,
    barTheirs: game?.position.bar.b ?? 0,
    offTheirs: game?.position.off.b ?? 0,
    winner: result?.winner ?? null,
    mySide: "w",
    locale,
  });

  if (!game) return null;

  const position = game.position;
  // Offline the kid is always white; online the seat comes from the session.
  const mySide: Turn = online?.side ?? "w";
  const playerTurn = position.turn === mySide;
  // Server clock, mm:ss per seat (online, clocked games only).
  const fmtClock = (ms: number): string => {
    const total = Math.max(0, Math.floor(ms / 1000));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
  };
  const canRoll =
    phase === "playing" &&
    playerTurn &&
    !robotThinking &&
    !submitting &&
    !waiting &&
    game.dice === null &&
    // A pending double owns this turn until answered — the roll button waits.
    !game.cubeOffer;
  const dice = game.dice;
  const cubeValue = game.cube?.value ?? 1;
  const myOfferPending = game.cubeOffer != null && game.cubeOffer.by === mySide;
  const cubeLabel =
    game.cube?.owner === mySide
      ? t("bg-cube-owned-you", { n: cubeValue })
      : game.cube?.owner === "c" || game.cube === undefined
        ? t("bg-cube-owned-center", { n: cubeValue })
        : t("bg-cube-owned-them", { n: cubeValue });
  // Win copy from the HUMAN's seat — a hardcoded 'w' here once made an
  // online black player read white's banner.
  const winText = result ? t(backgammonWinKey(result, mySide)) : null;
  const status =
    phase === "gameover"
      ? winText
      : online?.spectating
        ? t("bg-spectating")
        : offerAgainstMe
          ? t("bg-cube-offer", { n: game.cubeOffer?.value ?? cubeValue * 2 })
          : myOfferPending
            ? t("bg-cube-waiting", { n: cubeValue * 2 })
            : waiting
              ? t("waiting-for-opponent")
              : submitting
                ? t("waiting-for-opponent")
                : robotThinking
                  ? t("robots-turn")
                  : playerTurn
                    ? game.dice
                      ? t("your-turn-short")
                      : t("bg-roll-prompt")
                    : online
                      ? t("waiting-for-opponent")
                      : t("robots-turn");

  const mine = result?.winner === mySide;
  const summary: RoundSummary = {
    round: 1,
    outcome: !result ? "neutral" : mine ? "win" : "lose",
    headline: winText ?? "",
    gameOver: true,
    rows: [
      {
        key: "you",
        label: who.you.name,
        portrait: who.you.portrait,
        delta: "",
        total: mine ? "1" : "0",
        you: true,
        leading: mine,
      },
      {
        key: "opponent",
        label: online ? t("table-opponent") : who.opponent.name,
        portrait: online ? null : who.opponent.portrait,
        delta: "",
        total: result && !mine ? "1" : "0",
        leading: !!result && !mine,
      },
    ],
  };

  return (
    <main
      className="mx-auto flex w-full max-w-[1500px] flex-col items-stretch gap-4 p-4 pt-4 lg:flex-row"
      data-appdom-scope="backgammon"
      data-testid="backgammon-screen"
    >
      <Sidebar>
        <aside
          className="flex w-full flex-col gap-3 lg:w-80"
          data-testid="bg-panel"
        >
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-extrabold text-berry">Backgammon</h1>
            <button
              type="button"
              className="inline-flex min-h-[48px] items-center rounded-lg px-3 font-bold text-accent underline underline-offset-2"
              onClick={onExit}
              data-testid="back-home"
            >
              {t("home")}
            </button>
          </div>
          <div
            className="glass glass-light rounded-xl px-3 py-2 text-sm font-bold text-ink"
            data-testid="bg-status"
            data-appdom-id="bg-status"
            data-appdom-type="Text"
          >
            {status}
          </div>
          {online && !online.spectating && offerAgainstMe && (
            <div
              className="glass glass-light flex flex-col gap-2 rounded-xl px-3 py-2"
              data-testid="bg-cube-offer"
              data-appdom-id="bg-cube-offer"
              data-appdom-type="Panel"
            >
              <p className="text-sm font-bold text-ink">
                {t("bg-cube-offer", {
                  n: game.cubeOffer?.value ?? cubeValue * 2,
                })}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="min-h-[48px] flex-1 rounded-xl bg-accent px-3 py-2 text-sm font-semibold text-accent-fg disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={takeCube}
                  disabled={submitting}
                  data-testid="bg-cube-take"
                  data-appdom-id="bg-cube-take"
                  data-appdom-type="Button"
                >
                  {t("bg-cube-take")}
                </button>
                <button
                  type="button"
                  className="min-h-[48px] flex-1 rounded-xl border border-border px-3 py-2 text-sm font-bold text-fg-muted hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={passCube}
                  disabled={submitting}
                  data-testid="bg-cube-pass"
                  data-appdom-id="bg-cube-pass"
                  data-appdom-type="Button"
                >
                  {t("bg-cube-pass")}
                </button>
              </div>
            </div>
          )}
          {!online && (
            <div
              className="flex gap-2"
              role="radiogroup"
              aria-label="Robot difficulty"
              data-testid="bg-robot-level"
            >
              {([undefined, 1, 2] as const).map((depth) => {
                const id =
                  depth === undefined
                    ? "bg-robot-easy"
                    : depth === 1
                      ? "bg-robot-clever"
                      : "bg-robot-genius";
                return (
                  <button
                    key={id}
                    type="button"
                    aria-pressed={robotDepth === depth}
                    onClick={() => setRobotDepth(depth)}
                    data-testid={id}
                    data-appdom-id={id}
                    data-appdom-type="Button"
                    className={`flex-1 rounded-xl border px-3 py-2 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                      robotDepth === depth
                        ? "border-accent bg-accent text-accent-fg shadow-sm"
                        : "border-border bg-surface text-fg-muted hover:text-fg"
                    }`}
                  >
                    {depth === undefined
                      ? "Easy"
                      : depth === 1
                        ? "Clever"
                        : "Genius"}
                  </button>
                );
              })}
            </div>
          )}
          {!online?.spectating && (
            <button
              type="button"
              className="min-h-[48px] rounded-xl bg-accent px-4 py-2 font-display text-base font-semibold text-accent-fg shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
              onClick={roll}
              disabled={!canRoll}
              data-testid="bg-roll"
              data-appdom-id="bg-roll"
              data-appdom-type="Button"
            >
              {t("bg-roll")}
            </button>
          )}
          {/* The dice, as text: "3-5", doubles "4-4-4-4". A kid reads the
              numbers faster than pips at this size, and the audit can assert
              the exact roll through ?dice=. */}
          <div
            className="text-center font-display text-3xl font-extrabold tracking-widest text-ink"
            data-testid="bg-dice"
            data-appdom-id="bg-dice"
            data-appdom-type="Text"
          >
            {dice ? dice.join("-") : "·"}
          </div>
          {/* The doubling cube — online only (offline has no robot cube
              policy). Value + who owns it, then the offer button. */}
          {online && !online.spectating && (
            <>
              <div
                className="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-bold text-ink glass glass-light"
                data-testid="bg-cube"
                data-appdom-id="bg-cube"
                data-appdom-type="Text"
              >
                <span>{cubeLabel}</span>
                <span className="font-display text-xl font-extrabold">
                  {cubeValue}
                </span>
              </div>
              <button
                type="button"
                className="min-h-[48px] rounded-xl border border-border px-4 py-2 font-display text-base font-semibold text-fg shadow-sm hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
                onClick={offerDouble}
                disabled={!doubles}
                data-testid="bg-double"
                data-appdom-id="bg-double"
                data-appdom-type="Button"
              >
                {t("bg-double")}
              </button>
            </>
          )}
          <p
            className="glass glass-light rounded-xl px-3 py-2 text-sm font-bold text-ink"
            data-testid="bg-hint"
            data-appdom-id="bg-hint"
            data-appdom-type="Text"
          >
            {t("bg-hint")}
          </p>
          <div className="flex gap-2">
            {!online && (
              <button
                type="button"
                className="min-h-[48px] flex-1 rounded-xl border border-border px-3 py-2 text-sm font-bold text-fg-muted hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
                onClick={undo}
                disabled={phase !== "playing" || robotThinking}
                data-testid="bg-undo"
                data-appdom-id="bg-undo"
                data-appdom-type="Button"
              >
                Undo
              </button>
            )}
            {!online && (
              <button
                type="button"
                className="min-h-[48px] flex-1 rounded-xl border border-border px-3 py-2 text-sm font-bold text-fg-muted hover:text-fg"
                onClick={restart}
                data-testid="bg-restart"
                data-appdom-id="bg-restart"
                data-appdom-type="Button"
              >
                Restart
              </button>
            )}
          </div>
        </aside>
      </Sidebar>
      <section className="relative min-w-0 flex-1 overflow-x-clip">
        <div className="mx-auto flex w-full flex-col gap-2 lg:max-w-[max(22rem,calc(100dvh-19rem))]">
          <PlayerBar
            testid="player-bar-opponent"
            name={online ? t("table-opponent") : who.opponent.name}
            portrait={online ? null : who.opponent.portrait}
            fallbackIcon={who.opponent.fallbackIcon}
            active={phase === "playing" && !playerTurn}
            bubble={botBubble}
          >
            {online && clock && (
              <span
                className="rounded-xl bg-surface px-3 py-1 font-display font-extrabold"
                data-testid="bg-clock-them"
                data-appdom-id="bg-clock-them"
                data-appdom-type="Text"
              >
                {fmtClock(mySide === "w" ? clock.b : clock.w)}
              </span>
            )}
            <span
              data-testid="bg-pip-robot"
              data-appdom-id="bg-pip-robot"
              data-appdom-type="Text"
            >
              {t("bg-pips")}: {pipCount(position, "b")}
            </span>
            <span
              data-testid="bg-off-robot"
              data-appdom-id="bg-off-robot"
              data-appdom-type="Text"
            >
              {t("bg-home")}: {position.off.b}
            </span>
          </PlayerBar>
          <div
            className="relative aspect-square w-full rounded-3xl bg-board-frame max-sm:-mx-4 max-sm:w-[calc(100%+2rem)] max-sm:max-w-none max-sm:overflow-hidden max-sm:rounded-none shadow-xl"
            data-appdom-id="backgammon-board"
            data-appdom-type="Board"
          >
            <Canvas
              dpr={[1, 2]}
              camera={boardCam}
              onCreated={markBoardReady}
            >
              <BackgammonBoard
                position={position}
                onOverlayTransform={setOverlayTransform}
              />
            </Canvas>
            <BackgammonTapLayer
              position={position}
              selected={selected}
              targets={targets}
              playerColor="w"
              onTap={tap}
              ready={boardReady}
              movable={movable}
              risks={risks}
              transform={overlayTransform}
            />
            {phase === "gameover" && (
              <div
                className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-3xl bg-ink/50 text-2xl font-extrabold text-cream"
                data-testid="game-over"
                data-appdom-id="game-over"
                data-appdom-type="Banner"
              >
                <span className="inline-flex">
                  {result?.winner === "w" ? (
                    <Icon name="PartyPopper" size="lg" className="text-sun" />
                  ) : null}
                </span>
                <span>{winText}</span>
                {online && result && (
                  <span
                    className="text-base font-bold"
                    data-testid="bg-stake"
                    data-appdom-id="bg-stake"
                    data-appdom-type="Text"
                  >
                    {t("bg-stake", { n: backgammonStake(result, game.cube) })}
                  </span>
                )}
              </div>
            )}
          </div>
          <PlayerBar
            testid="player-bar-you"
            name={who.you.name}
            portrait={who.you.portrait}
            fallbackIcon={who.you.fallbackIcon}
            you
            active={phase === "playing" && playerTurn}
          >
            {online && clock && (
              <span
                className="rounded-xl bg-surface px-3 py-1 font-display font-extrabold"
                data-testid="bg-clock-you"
                data-appdom-id="bg-clock-you"
                data-appdom-type="Text"
              >
                {fmtClock(mySide === "w" ? clock.w : clock.b)}
              </span>
            )}
            <span
              data-testid="bg-pip-you"
              data-appdom-id="bg-pip-you"
              data-appdom-type="Text"
            >
              {t("bg-pips")}: {pipCount(position, "w")}
            </span>
            <span
              data-testid="bg-off-you"
              data-appdom-id="bg-off-you"
              data-appdom-type="Text"
            >
              {t("bg-home")}: {position.off.w}
            </span>
          </PlayerBar>
          {phase === "gameover" && result && !online?.spectating && (
            <RoundSummaryPanel
              testid="game-summary"
              summary={summary}
              nextLabel={t("table-play-again")}
              nextTestid="game-summary-again"
              onNext={restart}
            />
          )}
        </div>
      </section>
    </main>
  );
}
